import React from "react";
import { useForm } from "react-hook-form";
import { Form, FormActions, FormFields, Stack, FormTimeField, ensure } from "sprint-common-web";

import { convertTimestampToTimestring } from "../../../../../../../../../common/utils/convertTimestampToTimestring";
import { useVideoPreviewContext } from "../../../video/hooks/useVideoPreviewContext";
import { useVideoRekognitionResultContext } from "../../hooks/useVideoRekognitionResultContext";
import { useOverwriteSegmentForm } from "./useOverwriteSegmentForm";

export interface OverwriteSegmentFormProps {
    segmentId: string;
    onCancel(): void;
    onSubmit(): void;
}

export interface OverwriteSegmentFormInputs {
    startTimestamp: string;
    endTimestamp: string;
}

export const OverwriteSegmentForm = (props: OverwriteSegmentFormProps): JSX.Element => {
    const { items } = useVideoRekognitionResultContext();
    const { videoDuration } = useVideoPreviewContext();

    const itemToEdit = items.find((item) => item.key === props.segmentId);
    ensure(itemToEdit);

    const { errors, control, reset, handleSubmit, formState, watch } = useForm<OverwriteSegmentFormInputs>({
        defaultValues: {
            startTimestamp: convertTimestampToTimestring(itemToEdit?.startTimestamp),
            endTimestamp: convertTimestampToTimestring(itemToEdit?.endTimestamp)
        },
        criteriaMode: "all"
    });

    const { updateRekognitionSegment } = useOverwriteSegmentForm();

    const handleOverwriteSegment = async (inputs: OverwriteSegmentFormInputs) => {
        await updateRekognitionSegment(itemToEdit.key, inputs);
        props.onSubmit();
        reset();
    };

    const handleCancelButtonClick = () => {
        reset();
        props.onCancel();
    };

    return (
        <Form onSubmit={handleSubmit(handleOverwriteSegment)}>
            <Stack verticalAlign="space-between">
                <FormFields>
                    <FormTimeField
                        name="startTimestamp"
                        control={control}
                        errors={errors}
                        label="Start time"
                        value={convertTimestampToTimestring(itemToEdit.startTimestamp)}
                        maxValue={watch().endTimestamp}
                    />
                    <FormTimeField
                        name="endTimestamp"
                        control={control}
                        errors={errors}
                        label="End time"
                        value={convertTimestampToTimestring(itemToEdit.endTimestamp)}
                        maxValue={convertTimestampToTimestring(videoDuration)}
                        minValue={watch().startTimestamp}
                    />
                </FormFields>
                <FormActions
                    isLoading={formState.isSubmitting || formState.isValidating}
                    onCancel={handleCancelButtonClick}
                />
            </Stack>
        </Form>
    );
};
